import { computed, DestroyRef, effect, inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AppointmentStatusCode,
  OperatoryAppointmentSchedule,
  OperatoryAppointmentsResponse,
  PmsType,
  PracticeOperatory,
  Provider,
  ProviderRole,
  ScheduleDataService,
  SitesDataService,
  SiteSettings,
  UpdateAppointmentStatus,
} from '@ffd/api-client';
import { DateTime } from 'luxon';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ScheduleCalendarVM, ScheduleDetailsVM } from '@libs/schedule';
import {
  createAppointmentActions,
  filterMyDayAppointments,
  filterOperatoryAppointments,
  mapCreateAppointmentToOperatoryAppointment,
  mapCreateAppointmentToScheduleCalendarElement,
  mapMyDayAppointmentsWithEdit,
  mapOperatoryAppointmentsWithEdit,
  mapProviderColorToColor,
  ScheduleCalendarElement,
  ScheduleCalendarTimeWithOperatory,
  selectCurrentUserProfile,
  selectNewAppointment,
  ScheduleOperatoryProviderAppointment,
  selectEditedAppointment,
  selectDefaultAppointmentLength,
  selectTimeBlock,
  AppointmentActionType,
  mapMyDayAppointmentToScheduleCalendarElement,
  mapNewAppointmentToMyDayScheduleCalendarElement,
  mapOperatoryAppointmentProviderToCurrentProvider,
  selectUserSiteId,
  ScheduleOperatoryAppointment,
  mapOperatoryAppointmentToScheduleOperatoryAppointment,
  mapCreateAppointmentToCreateAppointmentRequest,
  mapCreateAppointmentToEditAppointmentRequest,
  DEFAULT_PRIMARY_COLOR,
  getAdjustedTimes,
  updateOperatoryAppointments,
  updateMyDayAppointments,
  EventDetailsVM,
  selectStatusOptions,
  selectHealthHistorySeverity,
  medicalAlertDefinitionActions,
  SelectProviderService,
  HealthHistoryStatus,
  filterMedicalAlertsToShow,
  filterPatientAlertsToShow,
  filterPatientAlertByDate,
  updateAppointmentInOperatories,
  updateAppointmentInMyDay,
  selectIsUserCollabOnlyAtSite,
  selectSiteSettings,
  selectSiteSettingsPmsType,
} from '@libs/store';
import {
  adjustMinuteToTimeBlock,
  adjustTime,
  DataState,
  DISABLE_GLOBAL_ERROR_TOASTS,
  FfdComponentStore,
  localStorageKey,
  routes,
  SKIP_CACHE,
  UNKNOWN_PROVIDER_NAME,
} from '@libs/utils';
import { tapResponse, concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { switchMap, tap, Observable, merge, of, EMPTY, filter } from 'rxjs';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { OnboardingModalService, OnboardingTab } from '@libs/onboarding';
import { AppointmentPayload } from '@libs/fsa';
import { AlertActionButtonsService, MedicalAlert, medicalAlertLocalStorageId, PatientAlert, ToastService } from '@libs/shared-services';
import { DropdownItem } from '@libs/ui-lib';
import { PatientSelectorService } from '../patient-selector/patient-selector.service';

const APPOINTMENT_LENGTH_MULTIPLIER = 3;

export interface LocalOperatoryAppointmentSchedule extends Omit<OperatoryAppointmentSchedule, 'appointments'> {
  appointments: Array<OperatoryAppointmentWithEdit>;
}

export interface OperatoryAppointmentWithEdit extends ScheduleOperatoryAppointment {
  isEdited?: boolean;
}

export interface SchedulesState {
  allOperatoryOptions: PracticeOperatory[];
  operatoryAppointments: LocalOperatoryAppointmentSchedule[];
  myDayAppointments: ScheduleOperatoryProviderAppointment[];
  internalProviders: Provider[];
  selectedDate: DateTime;
  selectedAppointmentId: number | null;
  selectedEventId: string | null;
  selectedOperatoryIds: string[] | undefined;
  scheduleState: DataState;
  silentReloadState: DataState;
  showTentative: boolean;
  isCompactView: boolean;
  saveInProgress: boolean;
  sidebarErrorMessage?: { title: string; message: string };
}

export interface SchedulesVM {
  siteId: string;
  myDay: ScheduleCalendarElement[];
  selectedOperatoryAppointments: ScheduleCalendarVM[];
  selectedDate: DateTime;
  selectedAppointment?: ScheduleDetailsVM;
  selectedEvent?: EventDetailsVM;
  operatoriesDropdownElements: DropdownItem<string>[];
  selectedOperatoryIds: string[];
  allProviders: Provider[];
  scheduleState: DataState;
  showTentative: boolean;
  isCompactView: boolean;
  isToday: boolean;
  saveInProgress: boolean;
  sidebarErrorMessage?: { title: string; message: string };
}

const initialState: SchedulesState = {
  allOperatoryOptions: [],
  operatoryAppointments: [],
  myDayAppointments: [],
  internalProviders: [],
  selectedDate: DateTime.now(),
  selectedOperatoryIds: undefined,
  selectedAppointmentId: null,
  selectedEventId: null,
  scheduleState: DataState.Initial,
  silentReloadState: DataState.Initial,
  showTentative: JSON.parse(localStorage.getItem(localStorageKey.showTentativeAppointments) || 'true'),
  isCompactView: JSON.parse(localStorage.getItem(localStorageKey.isCompactView) || 'false'),
  saveInProgress: false,
};

@Injectable({ providedIn: 'root' })
export class SchedulesService extends FfdComponentStore<SchedulesState, SchedulesVM> {
  private readonly route = inject(ActivatedRoute);
  private readonly sitesDataService = inject(SitesDataService);
  private readonly scheduleDataService = inject(ScheduleDataService);
  private readonly patientSelectorService = inject(PatientSelectorService);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly onboardingModalService = inject(OnboardingModalService);
  private readonly providerService = inject(SelectProviderService);
  public readonly allProviders = this.providerService.providers;
  private readonly alertActionButtonsService = inject(AlertActionButtonsService);

  public readonly isScheduleLoadingSignal = this.selectSignal((s) => s.scheduleState === DataState.Loading);
  public readonly internalProvidersSignal = this.selectSignal((s) => s.internalProviders);
  private readonly currentUser = this.store.selectSignal(selectCurrentUserProfile);
  private readonly siteSettings = this.store.selectSignal(selectSiteSettings);

  private readonly pmsType = this.store.selectSignal(selectSiteSettingsPmsType);
  private readonly isOpenDental = computed(() => this.pmsType() === PmsType.OPEN_DENTAL);

  constructor() {
    super(initialState);

    effect(() => {
      const settings = this.siteSettings();

      if (settings) {
        this.setAppointmentSetting(settings);
      }
    });
  }

  public reset(): void {
    this.patchState({
      allOperatoryOptions: [],
      operatoryAppointments: [],
      myDayAppointments: [],
      selectedDate: DateTime.now(),
      saveInProgress: false,
      sidebarErrorMessage: undefined,
      selectedOperatoryIds: undefined,
    });
  }

  public override init(): void {
    this.patientSelectorService.init();
    this.setSelectedDateFromLocalStorage();
    this.onboardingModalService.setActiveTab(OnboardingTab.Schedule);
    this.alertActionButtonsService.init();
    this.fetchAppointments({});
  }

  public setDate(date: DateTime): void {
    this.saveSelectedDateToLocalStorage(date);
    this.patchState({ selectedDate: date });
    this.fetchAppointments({});
  }

  public readonly setAppointmentId = this.updater((state, appointmentId: number | null): SchedulesState => {
    const currentDay = this.state().selectedDate.toFormat('yyyy-MM-dd');
    const siteId = this.store.selectSignal(selectUserSiteId)() ?? routes.showcaseSiteId;
    const selectedOperatoryProviderAppointment: ScheduleOperatoryProviderAppointment | undefined = this.state()
      .operatoryAppointments?.flatMap((operatory) =>
        operatory.appointments.map((a) => ({
          appointment: a,
          selectedDate: currentDay,
          operatoryName: operatory.operatoryName || operatory.id,
          operatoryId: operatory.id,
        })),
      )
      .find((app) => app.appointment.id === appointmentId);
    const patientId = selectedOperatoryProviderAppointment?.appointment?.patientId;

    this.store.dispatch(createAppointmentActions.onSetSelectedAppointment({ selectedAppointment: selectedOperatoryProviderAppointment }));
    if (patientId) {
      this.store.dispatch(
        medicalAlertDefinitionActions.getMedicalAlertDefinitions({
          siteId,
          patientId,
        }),
      );
    }
    return {
      ...state,
      selectedAppointmentId: appointmentId ?? null,
      selectedEventId: null,
    };
  });

  public getAppointmentDataWithProcedures(appointmentId: number): ScheduleOperatoryProviderAppointment | undefined {
    const currentDay = this.state().selectedDate.toFormat('yyyy-MM-dd');
    return this.state()
      .operatoryAppointments?.flatMap((operatory) =>
        operatory.appointments.map((a) => ({
          appointment: a,
          selectedDate: currentDay,
          operatoryName: operatory.operatoryName || operatory.id,
          operatoryId: operatory.id,
        })),
      )
      .find((app) => app.appointment.id === appointmentId);
  }

  public readonly setEventId = this.updater((state, eventId: string): SchedulesState => {
    return {
      ...state,
      selectedAppointmentId: null,
      selectedEventId: eventId ?? null,
    };
  });

  public readonly setNewAppointmentWithCalendar = this.updater(
    (state, { hour, minute, operatory }: ScheduleCalendarTimeWithOperatory): SchedulesState => {
      const defaultAppointmentLength = this.store.selectSignal(selectDefaultAppointmentLength);

      this.store.dispatch(
        createAppointmentActions.onSetCreateAppointment({
          operatory,
          fromTime: adjustTime({ hour, minute }),
          toTime: adjustTime({ hour, minute: minute + defaultAppointmentLength() }),
          appointmentDate: state.selectedDate,
        }),
      );

      return {
        ...state,
        sidebarErrorMessage: undefined,
        selectedAppointmentId: null,
        selectedEventId: null,
      };
    },
  );

  public setNewAppointmentWithButton = this.updater((state): SchedulesState => {
    const defaultAppointmentLength = this.store.selectSignal(selectDefaultAppointmentLength);
    const timeBlock = this.store.selectSignal(selectTimeBlock);

    const date = new Date();
    const hour = date.getHours();
    const minute = adjustMinuteToTimeBlock({ minute: date.getMinutes(), timeBlock: timeBlock() });

    const firstOperatory = state.allOperatoryOptions[0];
    if (!firstOperatory) {
      return state;
    }

    const operatory = state.allOperatoryOptions.find((o) => o.id === firstOperatory.id) ?? state.allOperatoryOptions[0];

    this.store.dispatch(
      createAppointmentActions.onSetCreateAppointment({
        operatory,
        fromTime: adjustTime({ hour, minute }),
        toTime: adjustTime({ hour, minute: minute + defaultAppointmentLength() }),
        appointmentDate: state.selectedDate,
      }),
    );

    return { ...state, selectedAppointmentId: null, sidebarErrorMessage: undefined };
  });

  public readonly removeAppointmentSelection = this.updater(
    (state): SchedulesState => ({
      ...state,
      selectedAppointmentId: null,
      selectedEventId: null,
      sidebarErrorMessage: undefined,
    }),
  );

  public showTentative = this.updater((state, showTentative: boolean): SchedulesState => ({ ...state, showTentative }));
  public setShowTentative = (showTentative: boolean): void => {
    this.showTentative(showTentative);
    // According to the design: If users switches the tentative filter off once, then it stays up until they turn it on again
    localStorage.setItem(localStorageKey.showTentativeAppointments, JSON.stringify(showTentative));
  };
  public updateIsCompactView = this.updater((state, isCompactView: boolean): SchedulesState => ({ ...state, isCompactView }));
  public setIsCompactView = (isCompactView: boolean): void => {
    this.updateIsCompactView(isCompactView);
    localStorage.setItem(localStorageKey.isCompactView, JSON.stringify(isCompactView));
  };

  public readonly updateSelectedOperatoryIdsState = this.updater(
    (state, selectedOperatoryIds: string[]): SchedulesState => ({
      ...state,
      selectedOperatoryIds: [...selectedOperatoryIds],
    }),
  );

  public readonly setSelectedOperatoryIds = ({ selectedOperatoryIds }: { selectedOperatoryIds: string[] }): void => {
    this.updateSelectedOperatoryIdsState(selectedOperatoryIds);
    this.saveSelectedOperatoryIds({ selectedOperatoryIds });
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  protected override mapStateToViewModel(state: SchedulesState): SchedulesVM {
    const currentDay = this.state().selectedDate.toFormat('yyyy-MM-dd');
    const allAppointments = [
      ...(state.myDayAppointments ?? []),
      ...(state.operatoryAppointments?.flatMap((o) =>
        o.appointments.map(
          (a) =>
            ({
              appointment: a,
              selectedDate: currentDay,
              operatoryName: o.operatoryName || o.id,
              operatoryId: o.id,
            }) satisfies ScheduleOperatoryProviderAppointment,
        ),
      ) ?? []),
    ];

    const newAppointmentState = this.store.selectSignal(selectNewAppointment)();

    const selectedAppointment = allAppointments.find((a) => a.appointment.id === state.selectedAppointmentId);
    const siteId = this.store.selectSignal(selectUserSiteId)() ?? routes.showcaseSiteId;

    //if you add a new appointment, it should show up in my day
    const isShowcase = siteId === routes.showcaseSiteId;
    const currentProviderId = this.currentUser()?.sites.find((site) => site.id === siteId)?.provider?.id;
    let myDay = state.myDayAppointments?.flatMap((a) => mapMyDayAppointmentToScheduleCalendarElement(a, isShowcase));

    if (newAppointmentState.appointmentActionType === AppointmentActionType.Edit) {
      myDay = myDay?.filter((a) => a.id !== newAppointmentState.id);
    }
    if (newAppointmentState.selectedProvider?.id === currentProviderId) {
      myDay?.push(...mapNewAppointmentToMyDayScheduleCalendarElement(newAppointmentState));
    }

    //the visible operatories can be set in a dropdown, we need to show only the selected ones
    const selectedOperatoryAppointments = state.allOperatoryOptions
      .filter((o) => state.selectedOperatoryIds?.includes(o.id))
      .map((operatory) => {
        const operatoryAppointments = state.operatoryAppointments?.find((o) => o.id === operatory.id);
        const appointments =
          operatoryAppointments?.appointments.map((a) =>
            this.mapOperatoryAppointmentToScheduleCalendarElement({
              appointment: a,
              selectedDate: currentDay,
              operatoryName: operatory.name,
              operatoryId: operatory.id,
            }),
          ) ?? [];

        const exceptions =
          operatoryAppointments?.exceptions.map((e) => {
            return getAdjustedTimes(e.from, e.to, isShowcase, currentDay);
          }) ?? [];

        const events =
          operatoryAppointments?.events?.map((e) => ({
            id: e.id,
            title: e.description || '',
            date: e.from || currentDay,
            from: getAdjustedTimes(e.from, e.to, isShowcase, currentDay).from,
            to: getAdjustedTimes(e.from, e.to, isShowcase, currentDay).to,
            operatoryName: operatoryAppointments.operatoryName || e.operatoryId,
            color: e.color,
            notes: e.notes,
          })) ?? [];

        const shouldShowNewAppointment = newAppointmentState && operatory.id === newAppointmentState.operatory?.id;

        //we also want to show the new appointment in the UI
        const newAppointment = shouldShowNewAppointment
          ? mapCreateAppointmentToScheduleCalendarElement(
              newAppointmentState,
              state.saveInProgress || state.silentReloadState === DataState.Loading,
            )
          : undefined;

        let newAppointmentWithPreRegPatient: ScheduleCalendarElement | undefined;
        if (newAppointment && selectedAppointment && selectedAppointment.appointment.isPreRegistrationAppointment) {
          newAppointmentWithPreRegPatient = {
            ...newAppointment,
            isPreRegistrationAppointment: true,
            patientName: `${selectedAppointment?.appointment.preRegistrationPatientName} ${selectedAppointment?.appointment.preRegistrationPatientSurname}`,
          };
        }

        return {
          id: operatory.id,
          operatoryName: operatory.name || operatory.id,
          appointments,
          exceptions,
          events,
          newAppointment: selectedAppointment?.appointment.isPreRegistrationAppointment ? newAppointmentWithPreRegPatient : newAppointment,
        };
      });

    const allEvents = selectedOperatoryAppointments.flatMap((o) => o.events);

    return {
      myDay,
      selectedOperatoryAppointments: selectedOperatoryAppointments,
      selectedDate: state.selectedDate,
      selectedAppointment: selectedAppointment
        ? this.mapOperatoryAppointmentToScheduleDetails(selectedAppointment, siteId, isShowcase)
        : undefined,
      selectedEvent: this.getSelectedEvent(state.selectedEventId, allEvents),
      operatoriesDropdownElements: state.allOperatoryOptions.map((o) => ({ label: o.name || o.id, value: o.id })),
      selectedOperatoryIds: state.selectedOperatoryIds
        ? state.selectedOperatoryIds.filter((id) => state.allOperatoryOptions.some((o) => o.id === id))
        : state.allOperatoryOptions.map((o) => o.id),
      allProviders: state.internalProviders,
      scheduleState: state.scheduleState,
      showTentative: state.showTentative,
      isCompactView: state.isCompactView,
      siteId,
      isToday: state.selectedDate.toFormat('yyyy-MM-dd') === DateTime.now().toFormat('yyyy-MM-dd'),
      saveInProgress: state.saveInProgress,
      sidebarErrorMessage: state.sidebarErrorMessage,
    };
  }

  private getSelectedEvent(selectedEventId: string | null, allEvents: Array<EventDetailsVM>): EventDetailsVM | undefined {
    return selectedEventId ? allEvents.find((e) => e.id === selectedEventId) : undefined;
  }

  private readonly setSaveInProgress = this.updater((state, saveInProgress: boolean): SchedulesState => {
    return {
      ...state,
      saveInProgress: saveInProgress,
    };
  });

  private readonly setSidebarErrorMessage = this.updater(
    (state, sidebarErrorMessage: { title: string; message: string } | undefined): SchedulesState => {
      return {
        ...state,
        sidebarErrorMessage,
      };
    },
  );

  private readonly resetSidebarErrorMessage = this.updater((state): SchedulesState => {
    return {
      ...state,
      sidebarErrorMessage: undefined,
    };
  });

  private mapOperatoryAppointmentToScheduleCalendarElement(
    providerAppointment: ScheduleOperatoryProviderAppointment,
  ): ScheduleCalendarElement {
    const siteId = this.store.selectSignal(selectUserSiteId)();
    const isShowcase = siteId === routes.showcaseSiteId;
    const { selectedDate, operatoryName, operatoryId, appointment } = providerAppointment;
    const { from, to } = getAdjustedTimes(appointment.from, appointment.to, isShowcase, selectedDate);
    return {
      id: appointment.id,
      from,
      to,
      date: selectedDate,
      timePattern: appointment.timePattern,
      color: appointment.provider ? mapProviderColorToColor(appointment.provider.color) : DEFAULT_PRIMARY_COLOR,
      patientName: appointment.isPreRegistrationAppointment
        ? `${appointment.preRegistrationPatientName} ${appointment.preRegistrationPatientSurname}`
        : `${appointment.patientName} ${appointment.patientSurname}`,
      patientId: appointment.patientId ?? '',
      hasHealthHistory: appointment.patientMedicalAlert,
      providerName: appointment.provider ? `${appointment.provider.firstName} ${appointment.provider.lastName}` : UNKNOWN_PROVIDER_NAME,
      pmsProviderId: appointment.provider?.pmsId ?? '',
      reason: appointment.reason,
      operatoryName: operatoryName || operatoryId,
      isTentative: appointment.draft,
      isEdited: appointment.isEdited,
      isProvisional: appointment.isProvisional,
      isPreRegistrationAppointment: appointment.isPreRegistrationAppointment,
      statusId: appointment.statusId,
      statusColor: appointment.statusColor,
      statusDisplayName: appointment.statusDisplayName,
      notes: appointment.notes ?? '',
      medicalAlerts: filterMedicalAlertsToShow(appointment.medicalAlerts).map((alert) => alert.description),
      patientAlerts: filterPatientAlertsToShow(appointment.patientAlerts).map((alert) => alert.description ?? ''),
      siteId: siteId ?? '',
    };
  }

  private mapOperatoryAppointmentToScheduleDetails(
    providerAppointment: ScheduleOperatoryProviderAppointment,
    siteId: string,
    isShowcase: boolean,
  ): ScheduleDetailsVM {
    const { selectedDate, operatoryName, operatoryId, appointment } = providerAppointment;
    const statusOptions = this.store.selectSignal(selectStatusOptions)();
    const healthHistorySeverity = this.store.selectSignal(selectHealthHistorySeverity)();

    const criticalHealthHistoryItems =
      appointment.medicalAlerts
        ?.filter((item) => item.isCritical && item.status === HealthHistoryStatus.Active)
        .map((item) => item.description) ?? [];

    const medicalAlertItem =
      criticalHealthHistoryItems.length > 0
        ? ({
            patientId: appointment.patientId ?? '',
            localStorageId: medicalAlertLocalStorageId,
            type: 'medical',
            alerts: criticalHealthHistoryItems,
          } as MedicalAlert)
        : undefined;

    const patientAlertItems =
      appointment.patientAlerts
        ?.filter((item) => item.showInSchedule && filterPatientAlertByDate(item))
        .map(
          (item) =>
            ({
              patientId: appointment.patientId ?? '',
              localStorageId: item.id.toString(),
              type: 'patient',
              description: item.description,
              notes: item.noteText,
            }) as PatientAlert,
        ) ?? [];

    return {
      id: appointment.id,
      person: {
        firstName: appointment.isPreRegistrationAppointment ? appointment.preRegistrationPatientName : appointment.patientName,
        lastName: appointment.isPreRegistrationAppointment ? appointment.preRegistrationPatientSurname : appointment.patientSurname,
      },
      siteId,
      patientId: appointment.patientId,
      phoneNumber: appointment.patientPhone ?? '',
      healthHistorySeverity,
      medicalAlertItem,
      patientAlertItems,
      appointmentDetails: {
        reason: appointment.reason,
        date: selectedDate,
        time: getAdjustedTimes(appointment.from, appointment.to, isShowcase, selectedDate),
        operatoryname: operatoryName || operatoryId,
        provider: appointment.provider ? `${appointment.provider.firstName} ${appointment.provider.lastName}` : UNKNOWN_PROVIDER_NAME,
        status: statusOptions?.find((s) => s.id === appointment.statusId)?.description ?? 'None',
        statusColor: appointment.statusColor,
        statusDisplayName: appointment.statusDisplayName,
        notes: appointment.notes ?? '',
        additionalProvider: appointment.additionalProvider
          ? `${appointment.additionalProvider.firstName} ${appointment.additionalProvider.lastName}`
          : undefined,
        staff: appointment.staffName,
        isTentative: appointment.draft,
        isProvisional: appointment.isProvisional,
        isPreRegistrationAppointment: appointment.isPreRegistrationAppointment,
      },
    };
  }

  public readonly fetchAppointments = this.effect(
    (
      $: Observable<{
        silentReload?: boolean;
        provisionalId?: number;
        newAppointmentId?: number;
        keepSelectedOperatoryIds?: boolean;
        skipCache?: boolean;
        callback?: () => void;
      }>,
    ) =>
      $.pipe(
        concatLatestFrom(() => [this.select((s) => ({ selectedDate: s.selectedDate })), this.store.select(selectIsUserCollabOnlyAtSite)]),
        filter(([effect, date, isCollabOnly]) => {
          return isCollabOnly !== undefined && !isCollabOnly;
        }),
        tap(([effect]) => {
          if (effect.silentReload) {
            this.patchState({ silentReloadState: DataState.Loading });
          } else {
            this.patchState({ scheduleState: DataState.Loading });
          }
        }),
        // eslint-disable-next-line sonarjs/cognitive-complexity
        switchMap(([{ provisionalId, newAppointmentId, keepSelectedOperatoryIds, skipCache, callback }, date]) => {
          const siteId = this.store.selectSignal(selectUserSiteId)();
          const dateInIso = date.selectedDate.startOf('day').toISO() ?? '';
          const baseContext = new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true);

          // Create two separate HTTP contexts for the cached and non-cached requests
          const cachedContext = baseContext;
          const nonCachedContext = baseContext.set(SKIP_CACHE, true);

          if (skipCache) {
            // If skipCache is explicitly requested, we'll just use a single non-cached request
            return this.fetchScheduleBySite$(
              siteId ?? routes.showcaseSiteId,
              dateInIso,
              provisionalId,
              newAppointmentId,
              keepSelectedOperatoryIds,
              date,
              callback,
              false,
              nonCachedContext,
            );
          }

          // Make concurrent requests - one with cache and one without
          const cachedRequest$ = this.fetchScheduleBySite$(
            siteId ?? routes.showcaseSiteId,
            dateInIso,
            provisionalId,
            newAppointmentId,
            keepSelectedOperatoryIds,
            date,
            callback,
            true,
            cachedContext,
          );

          const nonCachedRequest$ = this.fetchScheduleBySite$(
            siteId ?? routes.showcaseSiteId,
            dateInIso,
            provisionalId,
            newAppointmentId,
            keepSelectedOperatoryIds,
            date,
            callback,
            false,
            nonCachedContext,
          );

          return merge(cachedRequest$, nonCachedRequest$);
        }),
      ),
  );

  /**
   * Helper method to fetch schedule by site and process the response
   */
  private fetchScheduleBySite$(
    siteId: string,
    dateInIso: string,
    provisionalId?: number,
    newAppointmentId?: number,
    keepSelectedOperatoryIds?: boolean,
    date?: { selectedDate: DateTime },
    callback?: () => void,
    useCache: boolean = false,
    context: HttpContext = new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
  ): Observable<OperatoryAppointmentsResponse> {
    return this.scheduleDataService
      .getScheduleBySite(siteId, dateInIso, undefined, useCache, undefined, undefined, {
        context,
      })
      .pipe(
        tapResponse(
          // eslint-disable-next-line sonarjs/cognitive-complexity
          (response) => {
            if (!date || !response) return;

            this.patientSelectorService.setDefaultSelectedPatient();

            const operatories = response.operatories.map((o) => ({
              id: o.id,
              name: o.name || o.id,
            }));

            const currentDay = date.selectedDate.toFormat('yyyy-MM-dd');

            const currentProvider = this.currentUser()?.sites.find((site) => site.id === siteId)?.provider;
            const isShowcase = siteId === routes.showcaseSiteId;

            // mock the actual provider in showcase so we can see the appointments in my day
            const operatoryAppointments = isShowcase
              ? response.operatoryAppointments.map((appointment) => ({
                  ...appointment,
                  appointments: appointment.appointments.map((a) => {
                    return mapOperatoryAppointmentProviderToCurrentProvider(a, currentProvider);
                  }),
                }))
              : response.operatoryAppointments.map((appointment) => ({
                  ...appointment,
                  appointments: appointment.appointments.map((a) => {
                    // We keep the newly created appointment's id to keep the selection after fetching
                    if (a.id === provisionalId && newAppointmentId) {
                      a.id = newAppointmentId;
                    }
                    return mapOperatoryAppointmentToScheduleOperatoryAppointment(a);
                  }),
                }));

            const myDayAppointments = isShowcase
              ? response.operatoryAppointments.flatMap((appointment) =>
                  appointment.appointments.map((a) => ({
                    operatoryId: '', //TODO
                    operatoryName: appointment.operatoryName,
                    appointment: mapOperatoryAppointmentProviderToCurrentProvider(a, currentProvider),
                    selectedDate: currentDay,
                  })),
                )
              : response.myDay.map((appointment) => ({
                  ...appointment,
                  appointment: mapOperatoryAppointmentToScheduleOperatoryAppointment(appointment.appointment),
                  selectedDate: currentDay,
                }));

            const filteredSelectedOperatoryIds = this.state().selectedOperatoryIds?.filter((id) => operatories.some((o) => o.id === id));

            // show all internal providers
            const internalProviders = this.allProviders().data?.filter((p) => p.role === ProviderRole.INTERNAL) || [];

            this.patchState({
              allOperatoryOptions: operatories,
              operatoryAppointments,
              myDayAppointments,
              internalProviders,
              selectedOperatoryIds:
                keepSelectedOperatoryIds || filteredSelectedOperatoryIds ? filteredSelectedOperatoryIds : operatories.map((o) => o.id),

              scheduleState: DataState.Fetched,
              silentReloadState: DataState.Fetched,
            });

            this.store.dispatch(createAppointmentActions.onSetAllOperatories({ allOperatories: operatories }));
            callback?.();
          },
          (error: HttpErrorResponse) => {
            // Only show error and reset state if both requests fail
            // If this is the second request that failed (after a successful one), don't show error
            if (this.state().scheduleState !== DataState.Fetched) {
              this.toastService.error(this.translateService.instant('error_message_server_error_schedule_by_site'));
              this.patchState({
                allOperatoryOptions: [],
                operatoryAppointments: [],
                myDayAppointments: [],
                internalProviders: [],
                selectedOperatoryIds: [],
                scheduleState: DataState.Error,
                silentReloadState: DataState.Error,
              });
            }
            return console.error(error);
          },
        ),
      );
  }

  private readonly createAppointment = this.effect(($: Observable<{ isTentative: boolean; isHidden?: boolean }>) =>
    $.pipe(
      switchMap((action) => {
        const siteId = this.store.selectSignal(selectUserSiteId)() ?? routes.showcaseSiteId;
        const newAppointment = this.store.selectSignal(selectNewAppointment)();

        const createdAppointment = mapCreateAppointmentToCreateAppointmentRequest(
          {
            ...newAppointment,
            isTentative: action.isTentative,
          },
          this.isOpenDental(),
        );

        this.resetSidebarErrorMessage();
        this.setSaveInProgress(true);

        return this.scheduleDataService
          .createAppointment(siteId, createdAppointment, undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            tapResponse(
              ({ provisionalId }) => {
                this.showSaveSuccessModal(
                  { type: 'create', isTentative: action.isTentative, isHidden: action.isHidden },
                  newAppointment.id,
                );
                this.fetchAppointments({
                  silentReload: true,
                  provisionalId,
                  newAppointmentId: newAppointment.id,
                  keepSelectedOperatoryIds: action.isHidden,
                  skipCache: true,
                  callback: () => {
                    this.setSaveInProgress(false);
                    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
                  },
                });
              },
              (error: HttpErrorResponse) => {
                this.setSaveInProgress(false);
                this.setSidebarErrorMessage({
                  title: this.translateService.instant('error_message_generic_schedule_create_appointment_title'),
                  message: this.translateService.instant('error_message_generic_schedule_appointment_content'),
                });
                this.editSelectedAppointment({ isEdited: true });
                console.error(error);
              },
            ),
          );
      }),
    ),
  );

  public deleteRealAppointment = this.effect(($: Observable<number>) =>
    $.pipe(
      switchMap((appointmentId) => {
        const siteId = this.store.selectSignal(selectUserSiteId)() ?? routes.showcaseSiteId;
        return this.scheduleDataService.deleteAppointment(siteId, appointmentId).pipe(
          tapResponse(
            () => {
              this.toastService.info(this.translateService.instant('schedule_delete_appointment_modal_delete_success'));
            },
            (error: HttpErrorResponse) => {
              this.toastService.error(this.translateService.instant('error_message_server_error_delete_appointment'));
              return console.error(error);
            },
          ),
        );
      }),
    ),
  );

  public readonly editAppointment = this.effect(($: Observable<{ isTentative: boolean; isHidden?: boolean }>) =>
    $.pipe(
      switchMap((action) => {
        const siteId = this.store.selectSignal(selectUserSiteId)() ?? routes.showcaseSiteId;
        const appointment = this.store.selectSignal(selectNewAppointment)();

        const updatedAppointment = mapCreateAppointmentToEditAppointmentRequest(
          { ...appointment, isTentative: action.isTentative },
          this.isOpenDental(),
        );
        this.setSaveInProgress(true);

        return this.scheduleDataService
          .updateAppointment(siteId, appointment.id, updatedAppointment, undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            tapResponse(
              () => {
                this.showSaveSuccessModal({ type: 'edit', isTentative: action.isTentative, isHidden: action.isHidden }, appointment.id);
                this.fetchAppointments({
                  silentReload: true,
                  keepSelectedOperatoryIds: action.isHidden,
                  skipCache: true,
                  callback: () => {
                    this.setSaveInProgress(false);
                    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
                    this.removeAppointmentSelection();
                  },
                });
              },
              (error: HttpErrorResponse) => {
                this.setSaveInProgress(false);
                this.setSidebarErrorMessage({
                  title: this.translateService.instant('error_message_generic_schedule_update_appointment_title'),
                  message: this.translateService.instant('error_message_generic_schedule_appointment_content'),
                });
                console.error(error);
              },
            ),
          );
      }),
    ),
  );

  private showSaveSuccessModal(
    payload: { type: 'create' | 'edit'; isTentative: boolean; isHidden?: boolean },
    operatoryAppointmentId: number,
  ): void {
    let messageKey = 'schedule_create_appointment_success';

    if (payload.isHidden) {
      messageKey = 'create_appointment_toast_created_hidden';
    }

    if (payload.isHidden && payload.isTentative) {
      messageKey = 'create_appointment_toast_created_tentative_hidden';
    }

    if (payload.isTentative && !payload.isHidden) {
      this.setShowTentative(true);
      messageKey = 'schedule_tentative_appointment_success';
    }

    if (payload.type === 'edit') {
      messageKey = 'schedule_modify_appointment_success';
    }

    this.toastService
      .success(
        this.translateService.instant(messageKey),
        this.translateService.instant('schedule_create_appointment_success_action_button'),
      )
      .actionClicked$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.setAppointmentId(operatoryAppointmentId));
  }

  public readonly saveSelectedOperatoryIds = this.effect(($: Observable<{ selectedOperatoryIds?: string[] }>) =>
    $.pipe(
      switchMap(({ selectedOperatoryIds }) => {
        const siteId = this.store.selectSignal(selectUserSiteId)() ?? routes.showcaseSiteId;
        return this.sitesDataService
          .updateSelectedOperatories(siteId, { selectedOperatoryIds }, undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            tapResponse(
              () => {},
              (error: HttpErrorResponse) => {
                return console.error(error);
              },
            ),
          );
      }),
    ),
  );

  public saveAppointment = (payload: { type: 'create' | 'edit'; isTentative: boolean; notes: string }): void => {
    this.store.dispatch(createAppointmentActions.onSetNotes({ notes: payload.notes }));

    const newAppointment = this.store.selectSignal(selectNewAppointment)();
    const state = this.state();
    const isAppointmentVisible = state.selectedOperatoryIds?.includes(newAppointment.operatory?.id ?? '');
    const operatoryId = newAppointment.operatory?.id ?? '';
    if (!state.selectedOperatoryIds?.includes(operatoryId)) {
      this.setSelectedOperatoryIds({ selectedOperatoryIds: [...(state.selectedOperatoryIds ?? []), operatoryId] });
    }

    const operatoryAppointment = mapCreateAppointmentToOperatoryAppointment({ ...newAppointment, isTentative: payload.isTentative });

    const siteId = this.store.selectSignal(selectUserSiteId)();
    const currentProvider = this.currentUser()?.sites.find((site) => site.id === siteId)?.provider;

    if (siteId === routes.showcaseSiteId) {
      //for showcase we need to put the newAppointment to the operatoryAppointments and myDayAppointments manually
      this.patchState({
        operatoryAppointments: updateOperatoryAppointments(state, operatoryAppointment, newAppointment.operatory),
        myDayAppointments: updateMyDayAppointments(state, operatoryAppointment, currentProvider, newAppointment.operatory) ?? [],
        selectedAppointmentId: undefined,
      });
      this.showSaveSuccessModal({ ...payload, isHidden: !isAppointmentVisible }, operatoryAppointment.id);
      this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
    } else {
      if (payload.type === 'create') {
        this.createAppointment({ isTentative: payload.isTentative, isHidden: !isAppointmentVisible });
      } else {
        this.editAppointment({ isTentative: payload.isTentative, isHidden: !isAppointmentVisible });
      }
    }
  };

  public deleteAppointment = this.updater((state): SchedulesState => {
    const selectedAppointment = this.store.selectSignal(selectEditedAppointment)()!;

    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
    const siteId = this.store.selectSignal(selectUserSiteId)();

    if (siteId !== routes.showcaseSiteId) {
      this.deleteRealAppointment(selectedAppointment.appointment.id);
    }

    return {
      ...state,
      operatoryAppointments: filterOperatoryAppointments(state, selectedAppointment.operatoryId, selectedAppointment.appointment.id),
      myDayAppointments: filterMyDayAppointments(state, selectedAppointment.appointment.id) ?? [],
      selectedAppointmentId: null,
    };
  });

  public editSelectedAppointment = this.updater((state, payload: { isEdited: boolean }): SchedulesState => {
    const selectedAppointment = this.store.selectSignal(selectEditedAppointment)();

    if (!selectedAppointment) return { ...state };

    const operatoryAppointments = mapOperatoryAppointmentsWithEdit(
      state,
      selectedAppointment.operatoryId,
      selectedAppointment.appointment.id,
      payload.isEdited,
    );
    const myDayAppointments = mapMyDayAppointmentsWithEdit(state, selectedAppointment.appointment.id, payload.isEdited);

    return {
      ...state,
      operatoryAppointments,
      myDayAppointments: myDayAppointments ?? [],
    };
  });

  private readonly getSelectedDateStorageKey = (): string => {
    const siteId = this.store.selectSignal(selectUserSiteId)() ?? routes.showcaseSiteId;
    return `${localStorageKey.lastVisitedScheduleDate}_${this.currentUser()?.id}_${siteId}`;
  };

  private readonly setSelectedDateFromLocalStorage = (): void => {
    const localStorageDate = localStorage.getItem(this.getSelectedDateStorageKey());
    if (localStorageDate) {
      this.patchState({
        selectedDate: DateTime.fromISO(JSON.parse(localStorageDate)),
      });
    }
  };

  private readonly saveSelectedDateToLocalStorage = (date: DateTime): void => {
    localStorage.setItem(this.getSelectedDateStorageKey(), JSON.stringify(date));
  };

  public handleSocketUpdate(data: AppointmentPayload): void {
    const siteId = this.store.selectSignal(selectUserSiteId)();
    if (data.siteId === siteId) {
      this.fetchAppointments({ silentReload: true });
    }
  }

  public readonly updateAppointmentStatusEffect = this.effect(
    (
      $: Observable<{ appointmentId: number; status: AppointmentStatusCode; isTentative: boolean; originalStatus?: AppointmentStatusCode }>,
    ) =>
      $.pipe(
        switchMap((action) => {
          const siteId = this.store.selectSignal(selectUserSiteId)() ?? routes.showcaseSiteId;

          // Use the new simplified UpdateAppointmentStatus model
          const updateStatusRequest: UpdateAppointmentStatus = {
            statusId: action.status.id,
          };

          this.setSaveInProgress(true);

          // Update appointment status and set provisional immediately for instant visual feedback
          this.setAppointmentProvisional({ appointmentId: action.appointmentId, isProvisional: true, status: action.status });

          // For showcase site, handle status update client-side only
          if (siteId === routes.showcaseSiteId) {
            this.setSaveInProgress(false);
            this.showSaveSuccessModal({ type: 'edit', isTentative: action.isTentative, isHidden: false }, action.appointmentId);
            this.store.dispatch(createAppointmentActions.onSetStatus({ status: action.status }));
            return of(EMPTY);
          }

          return this.scheduleDataService
            .setAppointmentStatus(siteId, action.appointmentId, updateStatusRequest, undefined, undefined, {
              context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
            })
            .pipe(
              tapResponse(
                () => {
                  // Show success modal for status update using existing pattern
                  this.showSaveSuccessModal({ type: 'edit', isTentative: action.isTentative, isHidden: false }, action.appointmentId);

                  this.fetchAppointments({
                    silentReload: true,
                    keepSelectedOperatoryIds: true,
                    skipCache: true,
                    callback: () => {
                      this.setSaveInProgress(false);
                    },
                  });
                  this.store.dispatch(createAppointmentActions.onSetStatus({ status: action.status }));
                },
                (error: HttpErrorResponse) => {
                  this.setSaveInProgress(false);
                  this.toastService.error(this.translateService.instant('error_message_server_error_update_appointment_status'));

                  // Revert to original status on failure
                  if (action.originalStatus) {
                    this.setAppointmentProvisional({
                      appointmentId: action.appointmentId,
                      isProvisional: false,
                      status: action.originalStatus,
                    });
                  }
                  console.error(error);
                },
              ),
            );
        }),
      ),
  );

  public updateAppointmentStatus(
    appointmentId: number,
    status: AppointmentStatusCode,
    isTentative: boolean,
    originalStatus?: AppointmentStatusCode,
  ): void {
    this.updateAppointmentStatusEffect({ appointmentId, status, isTentative, originalStatus });
  }

  private readonly setAppointmentProvisional = this.updater(
    (
      state: SchedulesState,
      { appointmentId, isProvisional, status }: { appointmentId: number; isProvisional: boolean; status?: AppointmentStatusCode },
    ): SchedulesState => {
      const appointmentUpdates: Partial<OperatoryAppointmentWithEdit> = {
        isProvisional,
        ...(status && { statusId: status.id, statusColor: status.statusColor }),
      };

      const operatoryAppointments = updateAppointmentInOperatories(state.operatoryAppointments, appointmentId, appointmentUpdates);

      const myDayAppointments = updateAppointmentInMyDay(state.myDayAppointments, appointmentId, appointmentUpdates);

      return { ...state, operatoryAppointments, myDayAppointments };
    },
  );

  private setAppointmentSetting(siteSettings: SiteSettings): void {
    // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
    this.store.dispatch(
      createAppointmentActions.onSetAppointmentUnitSize({
        defaultAppointmentLength: siteSettings.appointmentUnitSizeInMin * APPOINTMENT_LENGTH_MULTIPLIER,
        timeBlock: siteSettings.appointmentUnitSizeInMin,
      }),
    );

    // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
    this.store.dispatch(createAppointmentActions.onSetStatusOptions({ options: siteSettings.appointmentStatusCodes ?? [] }));
    if (siteSettings.selectedOperatoryIds) {
      this.updateSelectedOperatoryIdsState(siteSettings.selectedOperatoryIds);
    }
  }
}
